import React, { ReactNode, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
  ColumnDef,
  getExpandedRowModel,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
  TableCell,
  TableHead,
  TableContainer,
  Skeleton,
  TableFooter,
  Button,
  Badge,
  cn,
  TableFilter,
  LoadingIcon,
} from '@sib/ui';
import { useElementSize, useIsLoadingDelayed } from '../hooks';
import { SettingsIcon } from '../icons';
import { XIcon } from '../icons/XIcon';
import { ColumnVisibilitySettingsModal } from './ColumnVisibilitySettingsModal';
import { TablePagination, TablePaginationProps } from './TablePagination';
import { useGetSelectedRowOriginalData } from './use-get-selected-row-original-data';
import { useTableSettings } from './use-table-settings';
import { getRowStyling } from './utils';

const DEFAULT_LOADING_ROWS = 10;
interface DataTableProps<T> {
  rowCount?: number;
  data: T[];
  columns: ColumnDef<T, unknown>[];
  settings: ReturnType<typeof useTableSettings<T>>;
  minPage?: number;
  loading?: boolean;
  noResultsPlaceholder?: ReactNode;
  NoResultsContainerProps?: React.HTMLAttributes<HTMLTableCellElement>;
  withFixedColumnWidth?: boolean;
  withPagination?: boolean;
  withFooter?: boolean;
  tablePaginationProps?: Partial<TablePaginationProps>;
  withSettingsRow?: boolean;
  withoutSettingsButton?: boolean;
  preserveSelectedValues?: boolean;
  withDelayedLoading?: boolean;
  TableWrapperProps?: React.HTMLAttributes<HTMLDivElement> &
    React.RefAttributes<HTMLDivElement>;
  TableProps?: React.HTMLAttributes<HTMLTableElement>;
}

type WithSubRows<T> = T & { subRows?: T[] };

export const DataTable = <T,>({
  settings: {
    tableProps,
    pagination,
    onPaginationChange,
    columnFilters,
    onColumnFiltersChange,
    minPage,
    onOriginalRowSelectionChange,
  },
  rowCount,
  data,
  columns,
  noResultsPlaceholder,
  NoResultsContainerProps,
  TableWrapperProps,
  TableProps,
  withFixedColumnWidth = false,
  loading = false,
  withPagination = false,
  withFooter = false,
  tablePaginationProps,
  withSettingsRow = false,
  withoutSettingsButton = false,
  preserveSelectedValues = false,
  withDelayedLoading = false,
}: DataTableProps<T>) => {
  const [isSettingsOpen, setSettingsOpen] = useState(false);

  const isLoadingDelayed = useIsLoadingDelayed({
    loading,
  });

  const showNoResults = !loading && data.length === 0;
  const { width, ref } = useElementSize<HTMLTableElement>({
    enabled: showNoResults || (loading && withDelayedLoading),
  });

  const getSelectedRowOriginalData = useGetSelectedRowOriginalData({ data });
  const table = useReactTable({
    data,
    columns,
    rowCount,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableExpanding: true,
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row: T) => (row as WithSubRows<T>).subRows ?? [],
    manualFiltering: true,
    ...tableProps,
    onRowSelectionChange: updater => {
      tableProps?.onRowSelectionChange?.(updater);
      if (!preserveSelectedValues) return;
      onOriginalRowSelectionChange?.(
        getSelectedRowOriginalData(
          updater,
          tableProps.state?.rowSelection,
          tableProps.state?.originalRowSelection as T[]
        )
      );
    },
  });

  const { t } = useTranslation();
  const appliedFilters = columnFilters.filter(
    f =>
      f.value !== undefined && f.value !== null && String(f.value).trim() !== ''
  );

  const rowSelection = table.getState().rowSelection;

  const selectedRowsCount = useMemo(
    () => Object.entries(rowSelection).filter(([_, value]) => value).length,
    [rowSelection]
  );

  return (
    <TableContainer ref={ref}>
      {withSettingsRow && (
        <div className="bg-primary min-h-9 w-full rounded-tl-sm rounded-tr-sm px-4 pt-2">
          <div className="flex w-full gap-2 pb-2 sm:pb-0">
            {!withoutSettingsButton && (
              <Button
                type="button"
                variant="outline"
                className="bg-primary hover:text-primary h-[28px] border-white text-white"
                onClick={() => setSettingsOpen(true)}
              >
                <SettingsIcon className="size-5" />
              </Button>
            )}
            {selectedRowsCount > 0 && (
              <Badge variant="secondary" className="rounded-md">
                {t('selectedRows', {
                  count: selectedRowsCount,
                })}
              </Badge>
            )}
            {appliedFilters.length > 0 && (
              <div className="flex w-full items-center justify-between">
                <span className="text-sm text-white">
                  {t('appliedFilters', {
                    count: appliedFilters.length,
                  })}
                </span>
                <Button
                  className="text-white"
                  variant="ghost"
                  size="custom"
                  onClick={e => {
                    e.stopPropagation();
                    onColumnFiltersChange([]);
                  }}
                >
                  <XIcon className="size-3" />
                  {t('clearAllFilters')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      <Table
        {...TableProps}
        style={{
          ...(withFixedColumnWidth && {
            tableLayout: 'fixed',
          }),
          ...TableProps?.style,
        }}
        wrapperProps={TableWrapperProps}
        {...(showNoResults && {
          className: 'rounded-sm ',
          wrapperProps: {
            ...TableWrapperProps,
            className: cn('overflow-hidden', TableWrapperProps?.className),
          },
        })}
      >
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const sorting = header.column.getIsSorted();
                const canUserSort = header.column.getCanSort();
                const meta = header.column.columnDef.meta;
                const rawFilterValue = header.column.getFilterValue();
                const arrayVal = Array.isArray(rawFilterValue)
                  ? rawFilterValue
                  : [];
                const filterProp = meta?.filter && {
                  type: meta.filter,
                  value: arrayVal,
                  onChange: (vals: string[]) =>
                    header.column.setFilterValue(vals),
                  options: meta.filterOptions ?? [],
                  disabled: meta.filterDisabled,
                };
                const className = meta?.className;
                const aplicableClassName =
                  typeof className === 'string'
                    ? className
                    : className?.(null as T);

                const width = header.column.getSize();

                return (
                  <TableHead
                    key={header.id}
                    canSort={canUserSort}
                    style={{ width }}
                    tooltip={meta?.headerTooltip}
                    onClick={() => {
                      if (!canUserSort) return;
                      if (sorting && sorting === 'desc') {
                        return header.column.clearSorting();
                      }
                      if (sorting && sorting === 'asc') {
                        return header.column.toggleSorting(true);
                      }
                      header.column.toggleSorting(false);
                    }}
                    filter={filterProp as TableFilter}
                    clearFilter={() => header.column.setFilterValue(undefined)}
                    setFilter={(vals: string[]) =>
                      header.column.setFilterValue(vals)
                    }
                    filterProps={meta?.filterProps}
                    className={cn(
                      aplicableClassName,
                      'whitespace-nowrap',
                      header.index === 0 && '!bg-primary text-white',
                      header.index === headerGroup.headers.length - 1 &&
                        '!bg-primary text-white',
                      withSettingsRow &&
                        header.index === 0 &&
                        '!rounded-tl-none'
                    )}
                    {...(sorting && { sortDirection: sorting })}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        {withSettingsRow && (
          <ColumnVisibilitySettingsModal
            table={table}
            open={isSettingsOpen}
            onClose={() => setSettingsOpen(false)}
            onSave={tableProps?.onColumnVisibilityChange}
          />
        )}
        <TableBody className="relative">
          {loading ? (
            <>
              {withDelayedLoading && (
                <div
                  style={{ left: width / 2 }}
                  className={cn(
                    {
                      'opacity-100': isLoadingDelayed && !showNoResults,
                      'pointer-events-none opacity-0':
                        !isLoadingDelayed && !showNoResults,
                    },
                    'sticky top-1/2 z-1 flex w-auto max-w-0 -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-visible transition-opacity duration-300'
                  )}
                >
                  <p className="text-md left-1/2 flex min-w-fit items-center gap-2 whitespace-pre">
                    {t('components.dataTable.loading')}
                    <LoadingIcon />
                  </p>
                </div>
              )}

              {Array.from({
                length: pagination.pageSize || DEFAULT_LOADING_ROWS,
              }).map((_, index) => (
                <TableRow key={`table-row-skeleton-${index}`}>
                  <TableCell colSpan={columns.length}>
                    <Skeleton className="size-full rounded-sm bg-[#E8EFF6]" />
                  </TableCell>
                </TableRow>
              ))}
            </>
          ) : data.length > 0 ? (
            table.getRowModel().rows.map((row, index) => {
              return (
                <TableRow
                  key={row.id}
                  onClick={() => {
                    if (row.getCanExpand()) {
                      row.toggleExpanded();
                    }
                  }}
                  className={cn(
                    row.getCanExpand() && 'cursor-pointer',
                    getRowStyling({
                      row,
                      index,
                      rows: table.getRowModel().rows,
                    })
                  )}
                >
                  {row.getVisibleCells().map(cell => {
                    const className =
                      cell.getContext().column.columnDef.meta?.className;

                    const aplicableClassName =
                      typeof className === 'string'
                        ? className
                        : className?.(row.original);

                    const width = cell.column.getSize();

                    return (
                      <TableCell
                        key={cell.id}
                        className={aplicableClassName}
                        style={{ width }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          ) : noResultsPlaceholder ? (
            <TableRow className="hover:bg-white">
              <TableCell {...NoResultsContainerProps} colSpan={columns.length}>
                <div style={{ width }} className="sticky left-0 px-4">
                  {noResultsPlaceholder}
                </div>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
        {withFooter && (
          <TableFooter>
            {table.getFooterGroups().map(footerGroup => (
              <TableRow key={footerGroup.id}>
                {footerGroup.headers.map(header => (
                  <TableCell key={header.id}>
                    {flexRender(
                      header.column.columnDef.footer,
                      header.getContext()
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableFooter>
        )}
      </Table>
      {data.length > 0 && typeof rowCount === 'number' && withPagination && (
        <TablePagination
          onChange={onPaginationChange}
          rowCount={rowCount}
          state={pagination}
          minPage={minPage}
          {...tablePaginationProps}
        />
      )}
    </TableContainer>
  );
};
